/**
 * The seam between what someone asked for and what is close to it.
 *
 * A curated deck now continues past its exact matches (see useDeckContinuation
 * and the backend's widen.py), and this is the one card that says so. It sits
 * in the card stage at the exact position where the widened products begin,
 * with the first of them already visible behind it — so the offer and the
 * answer to it are on screen together, and continuing is one gesture.
 *
 * Why a card and not a toast: the alternative is appending near-misses
 * silently, which is the quiet substitution that erodes trust in a filter, and
 * it is the failure the feed's own widening message (feed.py) already exists to
 * prevent. A toast would say it and then take it away mid-swipe; a card is
 * something the shopper actually agrees to, in her voice, at the moment it
 * becomes true.
 *
 * Not a dead end in either direction. "Keep going" continues, but so does a
 * swipe — dismissal is handled by DiscoveryFeed, which treats any verdict on
 * this card as agreement and lets the gesture fall through to the deck
 * underneath. The only other way out is back to MAHI, to ask for something
 * else entirely.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import useReducedMotion from '../../hooks/useReducedMotion';

export default function DeckTransitionCard({ message, onContinue, onAskMahi }) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      data-testid="deck-transition-card"
      className="absolute inset-0 z-20 rounded-[1.75rem] overflow-hidden"
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduced ? { opacity: 0 } : { opacity: 0, y: -18, scale: 0.97 }}
      transition={reduced
        ? { duration: 0.2, ease: 'easeOut' }
        : { type: 'spring', stiffness: 260, damping: 30, mass: 0.9 }}
    >
      {/* Her own surface, not a product's: the mahiteal wash is reserved for
          MAHI's voice throughout this app, and raspberry stays on the action
          that continues shopping. */}
      <div className="absolute inset-0 bg-gradient-to-b from-mahiteal-50 via-white to-white ring-1 ring-mahiteal-100 rounded-[1.75rem]" />

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
        <motion.div
          initial={reduced ? false : { scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.08, type: 'spring', stiffness: 300, damping: 20 }}
          className="w-12 h-12 rounded-full bg-mahiteal-100 flex items-center justify-center mb-5"
        >
          <Sparkles className="w-6 h-6 text-mahiteal-600" strokeWidth={1.75} />
        </motion.div>

        <p className="font-mahi-serif text-[22px] leading-snug text-slate-900 max-w-[17rem]">
          {message}
        </p>

        <button
          type="button"
          onClick={onContinue}
          data-testid="deck-transition-continue"
          className="mt-7 px-7 py-3 rounded-full bg-raspberry-500 text-white text-[15px] font-medium font-mahi-sans shadow-[0_12px_28px_-12px_rgba(225,62,128,0.65)] active:scale-95 transition-transform"
        >
          Show me
        </button>

        <button
          type="button"
          onClick={onAskMahi}
          data-testid="deck-transition-ask"
          className="mt-3 text-[13px] text-slate-500 font-mahi-sans underline underline-offset-4 decoration-slate-300 active:scale-95 transition-transform"
        >
          Ask for something else
        </button>
      </div>
    </motion.div>
  );
}
